/**
 * FinancialProfilePage
 * Página standalone del perfil financiero. Wraps el componente compartido
 * `ProfileSection` (mismo que vive dentro del expediente) para mantener
 * consistencia visual entre ambas vistas.
 * Route: /credit/profiles/:directoryId
 */

import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { CopyableId } from '@/components/ui/CopyableId';
import { useFinancialProfile } from '@/features/credit/hooks';
import { ProfileSection } from '@/features/expedientes/components/ProfileSection';
import { ROUTES } from '@/constants';

export function FinancialProfilePage() {
  const { directoryId = '' } = useParams<{ directoryId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromCobranza = searchParams.get('from') === 'cobranza';
  const fromCandidatos = searchParams.get('from') === 'candidatos';
  const goBack = () => navigate(-1);

  // El endpoint de financial profile NO trae el nombre del cliente. La pantalla origen
  // (Candidatos, etc.) puede pasarlo via state de navegacion para mostrarlo aqui.
  const navState = location.state as { displayName?: string; phoneNumber?: string } | null;

  const { profile, isLoading, isFetching, isError, refetch } = useFinancialProfile(directoryId);

  const breadcrumbs = fromCobranza
    ? [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
        { label: 'Cartera', path: ROUTES.COLLECTION_CARTERA },
        { label: 'Perfil Financiero', path: '#' },
      ]
    : fromCandidatos
    ? [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
        { label: 'Scoring', path: ROUTES.SCORING },
        { label: 'Candidatos a Credito', path: ROUTES.CREDIT_CANDIDATOS },
        { label: 'Perfil Financiero', path: '#' },
      ]
    : [
        { label: 'Inicio', path: ROUTES.DASHBOARD },
        { label: 'Originacion', path: ROUTES.PIPELINE_MATRIX },
        { label: 'Perfil Financiero', path: '#' },
      ];

  // Identificar al cliente en el header. Prioridad:
  // 1) Nombre del propio endpoint (backend type-kit 3.57.0+). Funciona en cualquier ruta de entrada.
  // 2) Nombre del state de navegacion (Candidatos, etc.) como fallback mientras el backend no este deployado.
  // 3) Telefono cuando esta disponible (endpoint o state).
  // 4) DirectoryId truncado como ultimo recurso.
  const nameFromProfile = profile?.full_name?.trim();
  const navName = navState?.displayName?.trim();
  const displayName = nameFromProfile || navName;
  const navPhone = navState?.phoneNumber?.trim();
  const phoneFromProfile = profile?.phone_number?.trim();
  const phone = phoneFromProfile || navPhone;
  const headerSubtitle = displayName
    ? (phone ? `${displayName} · ${phone}` : displayName)
    : phone
      ? phone
      : isLoading
        ? 'Cargando datos del cliente...'
        : `DIR ${directoryId.slice(0, 8)}…`;

  const header = (
    <PageHeader
      title="Perfil Financiero"
      subtitle={headerSubtitle}
      breadcrumbs={breadcrumbs}
      onBack={goBack}
      actions={
        <Button onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={16} className={isFetching ? 'animate-spin' : ''} />
          {isFetching ? 'Actualizando...' : 'Actualizar'}
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <div>
        {header}
        <div className="space-y-3">
          <Skeleton height={70} variant="rectangular" />
          <Skeleton height={420} variant="rectangular" />
        </div>
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div>
        {header}
        <Card>
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertTriangle size={32} className="text-destructive" />
            <h3 className="text-lg font-semibold text-foreground">No se pudo cargar el perfil</h3>
            <p className="max-w-md text-sm text-muted-foreground">
              Verifica tu conexion e intenta de nuevo.
            </p>
            <Button variant="primary" onClick={() => refetch()}>
              Reintentar
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {header}

      {/* IDs copiables (solo en la vista standalone) */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <CopyableId label="Directory ID" value={directoryId} />
        {profile.people_id && <CopyableId label="People ID" value={profile.people_id} />}
      </div>

      <div className="rounded-md border border-border bg-card">
        <ProfileSection directoryId={directoryId} />
      </div>
    </div>
  );
}
